import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Clock, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_POSTS = [
  {
    id: "ooh-still-wins",
    title: "Why OOH Still Wins in a Digital-First World",
    excerpt: "Attention is the scarcest currency. Outdoor advertising delivers it at scale, without an unsubscribe button.",
    category: "Industry",
    read_time: "6 min",
    date: "2025-11-12",
    body: [
      "In a media landscape where every other channel is competing for fragmented, ad-blocked, scroll-fatigued attention, Out-of-Home stands apart for one simple reason: you cannot scroll past a city.",
      "OOH is the only mass medium that has not been disrupted by the smartphone. It is the only medium where attention is opt-in by default, where ad-blockers do not exist, and where a single creative can become a cultural moment overnight.",
      "The numbers back it up. According to recent industry studies, OOH delivers the highest cost-per-thousand attention seconds of any mass medium, outperforming TV by 2.5x and online video by nearly 4x. When measured for branded search lift and consideration uplift, OOH consistently sits in the top quartile of any media mix.",
      "But OOH only wins when it is treated as a strategic channel, not a back-up plan. The brands winning today are the ones investing in trophy locations, building creative for the format, and measuring outcomes the way they measure performance media.",
      "The opportunity is enormous. The infrastructure is finally there. The question is no longer whether OOH still wins. The question is whether your brand is ready to use it properly.",
    ],
  },
  {
    id: "programmatic-dooh",
    title: "Programmatic DOOH: A Practical Playbook",
    excerpt: "How to plan, buy, and measure programmatic outdoor in 2025, with real campaign benchmarks.",
    category: "Strategy",
    read_time: "9 min",
    date: "2025-10-30",
    body: [
      "Programmatic DOOH has matured from a buzzword into one of the most precise media buys available. Done well, it gives you the targeting of digital with the impact of physical.",
      "Step one is audience definition. Forget broad demographic buys. Modern programmatic DOOH lets you trigger creative based on weather, traffic, sports scores, store inventory, footfall density, and time of day. A coffee brand can dim screens during rain, a QSR can swap creative when a competitor location runs low, an OTT brand can change copy when a cricket match goes into the final over.",
      "Step two is bidding strategy. Treat DOOH like programmatic display. Set up audience-aligned PMPs with publishers, define dayparting based on commute and dwell-time data, and use frequency caps to avoid wasted impressions on the same eyeballs.",
      "Step three is creative discipline. Six words. One idea. Big visual. The medium does not reward complexity. The medium rewards immediate, recognisable, repeatable.",
      "Step four is measurement. Branded search lift, app installs, and footfall attribution are now table stakes. The brands extracting the most ROI from DOOH are the ones treating it as a measured, accountable channel, not a brand-equity placeholder.",
    ],
  },
  {
    id: "measuring-impact",
    title: "Measuring OOH Impact Beyond Impressions",
    excerpt: "From branded search lift to footfall attribution, modern OOH measurement, demystified.",
    category: "Measurement",
    read_time: "7 min",
    date: "2025-10-04",
    body: [
      "For decades, OOH was sold on impressions. The number was opaque, the methodology was approximate, and the buyer had to trust the seller. That era is over.",
      "Modern OOH measurement starts with mobility data. Anonymised mobile location signals can now tell you exactly how many unique devices passed a billboard, how long they dwelled, what other locations they visited that week, and whether they later visited your store or app.",
      "Branded search lift is the second pillar. Compare branded search volume in cities running your campaign vs. control cities. A well-executed OOH campaign typically lifts branded search by 15 to 40 percent within the campaign window.",
      "Footfall attribution is the third pillar. By comparing exposed and unexposed device cohorts, you can measure incremental store visits driven by your OOH spend with statistical confidence.",
      "Brand lift studies, app install attribution, and post-campaign surveys round out the toolkit. The brands that take OOH measurement seriously consistently outperform the brands that do not.",
    ],
  },
  {
    id: "creative-rules",
    title: "5 Creative Rules for Billboards That Convert",
    excerpt: "Six words. One idea. Zero excuses. The creative principles that separate forgettable from iconic.",
    category: "Creative",
    read_time: "5 min",
    date: "2025-09-18",
    body: [
      "Rule one: six words or fewer. The average viewer sees a billboard for 1.6 seconds. If your headline does not land in that window, it does not land at all.",
      "Rule two: one idea. Not three benefits, not a feature list, not a tagline plus a website plus a phone number. One idea, executed without compromise.",
      "Rule three: visual hierarchy. The brand should be unmistakable from 100 metres. The headline should be readable from 50. Everything else is decoration, and decoration is what makes outdoor forgettable.",
      "Rule four: build for the format. A digital banner does not become a billboard by enlarging it. The medium has its own language: bold typography, high-contrast colour, generous negative space.",
      "Rule five: make it culturally specific. The most iconic outdoor campaigns are the ones that feel native to the city they live in. Local references, local typography, local humour. Outdoor is the most public medium there is. Treat it that way.",
    ],
  },
];

const COVERS = [
  "https://images.pexels.com/photos/27207885/pexels-photo-27207885.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.unsplash.com/photo-1708107243374-52eddcc3fe64?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.pexels.com/photos/14338583/pexels-photo-14338583.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.unsplash.com/photo-1542936847-753ed0de28e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

export default function Blog() {
  const [posts, setPosts] = useState(FALLBACK_POSTS);
  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((r) => {
        if (r.data?.posts?.length) setPosts(r.data.posts);
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="blog-page" className="text-zinc-100">
      <section className="relative pt-36 md:pt-44 pb-20 border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/30 via-zinc-950 to-zinc-950" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="font-mono-accent text-[11px] text-amber-400 mb-5">Insights</div>
          <h1 className="font-display text-5xl md:text-7xl font-black leading-[0.95] tracking-tighter max-w-4xl">
            Field notes from <span className="gradient-text italic">the streets.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-zinc-400 leading-relaxed">
            Strategy, measurement, creative, and trends, built for marketers who
            take outdoor seriously.
          </p>
        </div>
      </section>

      <section className="bg-zinc-950">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-28 space-y-24">
          {posts.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start"
              data-testid={`blog-post-${p.id}`}
              id={p.id}
            >
              <div className="md:col-span-5 md:sticky md:top-24">
                <div className="relative overflow-hidden rounded-2xl border border-white/5 group">
                  <img
                    src={COVERS[i % COVERS.length]}
                    alt={p.title}
                    className="h-[280px] md:h-[360px] w-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-1000"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />
                </div>
                <div className="mt-5 flex items-center flex-wrap gap-3 text-[10px] font-mono-accent">
                  <span className="px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300">
                    {p.category}
                  </span>
                  <span className="flex items-center gap-1.5 text-zinc-500">
                    <Calendar className="h-3 w-3" />
                    {new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                  <span className="flex items-center gap-1.5 text-zinc-500">
                    <Clock className="h-3 w-3" /> {p.read_time}
                  </span>
                </div>
              </div>

              <div className="md:col-span-7">
                <div className="font-mono-accent text-[10px] text-zinc-500 mb-3">
                  Article {String(i + 1).padStart(2, "0")} / {String(posts.length).padStart(2, "0")}
                </div>
                <h2 className="font-display text-3xl md:text-5xl font-black leading-[1.05] tracking-tighter">
                  {p.title}
                </h2>
                <p className="mt-5 text-lg text-zinc-300 leading-relaxed font-medium">
                  {p.excerpt}
                </p>
                <div className="mt-7 space-y-5 text-zinc-400 leading-[1.8]">
                  {(p.body || []).map((para, idx) => (
                    <p key={idx} className="text-[15px] md:text-base">
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
